'use client'

import { IoHeart } from 'react-icons/io5'

import { cn } from '@/core/utils/common'

import { ChatIcon } from '@/features/veluvit/components/ui/action-icons'
import TagLine from '@/features/veluvit/components/ui/tag-line'
import { kfmt } from '@/features/veluvit/lib/format'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'

import type { Char } from '@/features/veluvit/types/catalog.types'

const SIZE = {
  /** 랭킹 1위 시상대 — 이름만 크게 */
  lg: {
    name: 'mt-1.5 text-c26 leading-[1.15] title-glow',
    line: 'text-c12 over10:text-c13',
    icon: 'size-3.5',
  },
  /** 홈 격자 카드 */
  md: {
    name: 'mt-2 text-c15 over10:text-c16',
    line: 'text-c12 over10:text-c13',
    icon: 'size-3.5',
  },
  /** 레일 카드 · 랭킹 줄 · 좁은 카드 */
  sm: {
    name: 'mt-1.75 text-c14 over10:text-c15',
    line: 'text-c11 over10:text-c12',
    icon: 'size-3',
  },
}

/**
 * 상품(작품) 목록 공통 글 묶음 — 모든 상품 리스트가 같은 순서 · 같은 모양으로 쓴다(2026-10-07 싱크).
 *   1. 상품명
 *   2. ♥ 좋아요 · 💬 대화 수 (출시 전이면 '출시 예정')
 *   3. #태그
 *   4. 제작자 · 제작자 등급(기준 미정 — 값이 오면 이름 옆 작은 배지, 지금은 자리만)
 * 한 줄 소개(hook)는 목록에서 뺐다 — 상세에서 본다. 줄마다 한 줄로 자른다.
 */
export default function ProductMeta({
  c,
  size = 'md',
  flush,
  className,
}: {
  c: Char
  size?: keyof typeof SIZE
  /** 그림과 나란히 놓이거나(줄 모양) 부모가 간격을 이미 줄 때 — 상품명 위 여백을 없앤다 */
  flush?: boolean
  className?: string
}) {
  const s = SIZE[size]
  const { catalog } = useCatalogMedia()
  const stats = catalog.works.find((work) => work.id === c.id)?.stats
  return (
    <span className={cn('block min-w-0', className)}>
      <span
        className={cn(
          'block truncate font-bold text-t1',
          s.name,
          flush && 'mt-0!'
        )}
      >
        {c.name}
      </span>
      {(c.preview ||
        typeof stats?.likes === 'number' ||
        typeof stats?.chats === 'number') && (
        <span
          className={cn(
            'mt-0.5 flex items-center gap-2.5 text-t3 tabular-nums',
            s.line
          )}
        >
          {c.preview ? (
            <span className="font-semibold text-gem">출시 예정</span>
          ) : (
            <>
              {typeof stats?.likes === 'number' && (
                <span
                  className="inline-flex items-center gap-0.75"
                  aria-label={`좋아요 ${stats.likes}`}
                >
                  <IoHeart aria-hidden="true" className={s.icon} />
                  {kfmt(stats.likes)}
                </span>
              )}
              {typeof stats?.chats === 'number' && (
                <span
                  className="inline-flex items-center gap-0.75"
                  aria-label={`대화 ${stats.chats}`}
                >
                  <ChatIcon aria-hidden="true" className={s.icon} />
                  {kfmt(stats.chats)}
                </span>
              )}
            </>
          )}
        </span>
      )}
      <TagLine
        tags={c.tags}
        max={size === 'sm' ? 3 : 4}
        size={size === 'sm' ? 'sm' : 'md'}
        className="mt-0.5"
      />
      <span
        className={cn('mt-0.5 flex min-w-0 items-center gap-1 text-t3', s.line)}
      >
        <span className="min-w-0 truncate">{c.author}</span>
        {/* 제작자 등급 자리 — 기준이 정해지면 값만 내려오면 된다 */}
        {c.creatorGrade && (
          <span className="flex-none rounded-control px-1 text-c10 leading-[1.6] font-bold text-t2 shadow-[inset_0_0_0_1px_var(--color-line2)]">
            {c.creatorGrade}
          </span>
        )}
      </span>
    </span>
  )
}
